// eslint-disable-next-line
import React, { useEffect, useState } from 'react';
import { GenericToolbar } from '../../components/GenericToolbar';
import { useHistory } from 'react-router-dom';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { ToolbarColor } from '../../components/GenericToolbar/GenericToolbar';
import { OrgInfo } from './OrgInfo';
import { OrgBuildings } from './OrgBuildings';
import { ENTITY_TYPE } from '../enum/ENUM';
import { Box, Button, Snackbar, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Selectors } from '../../selectors';
import { OrganizationActions } from '../../actions/actions/organization-actions';
import * as PXBColors from '@brightlayer-ui/colors';
import { pushAppRoute } from '../../data/domain/route-manager';
import { OverviewMap } from '../overview/OverviewMap';
import { Card } from '@mui/material';
import LastRetrievedTimestamp from '../../components/LastRetrievedTimestamp/LastRetrievedTimestamp';
import { OrganizationThunks } from '../../actions/thunks/organization-thunks';
import { DashboardThunks } from '../../actions/thunks/dashboard-thunks';

type Props = {
    location: any;
};

export const OrganizationDetails: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = (props: Props) => {
    const history = useHistory();
    const { t } = useLanguageLocale();
    const [orgName, setOrgName] = useState('');
    const theme = useTheme();
    const dispatch = useDispatch();
    const md = useMediaQuery(theme.breakpoints.up('md'));
    const organization = useSelector(Selectors.Organization.organization);
    const dashboard = useSelector(Selectors.Dashboard.dashboard);
    const [alertSuccessState, setAlertSuccessState] = useState(false);
    const [alertSuccessMessage, setAlertSuccessMessage] = useState<string>('');
    const [lastRetrievedTime, setLastRetrievedTime] = useState<undefined | Date>(undefined);

    const backRoute = props.location?.state?.backRoute || '';
    const orgId = props.location?.state?.orgId || null;

    const refreshOrgDetails = (): void => {
        if (!orgId) {
            return;
        }
        dispatch(OrganizationThunks.getOrganizationDetailsList(orgId));
        dispatch(DashboardThunks.getBuildingList(orgId));
    };

    const goToOrgBuildingDetails = (value: { buildingId: string, buildingName: string }): void => {
        pushAppRoute(history,
            { type: 'OrgBuildingDetails', buildingId: value.buildingId },
            { backRoute: 'OrganizationDetails', orgId: orgId, buildingId: value.buildingId });
    };

    const goToManageUsers = (): void => {
        pushAppRoute(history,
            { type: 'ManageAssetUsers', assetId: orgId },
            { backRoute: 'OrganizationDetails', assetId: orgId, assetType: ENTITY_TYPE.ORGANIZATION, title: orgName });
    };
    useEffect(() => {
        setOrgName(orgName)
    }, [orgName])

    useEffect(() => {
        if (organization.updateOrgDetailSuccess && !organization.updateOrgDetailLoading) {
            setAlertSuccessMessage(t('TOAST_MSG.TOAST_SUCCESS_ORG_UPDATE'));
            setAlertSuccessState(true)
            dispatch(OrganizationActions.updateOrgUnmounted());
        }
    }, [organization.updateOrgDetailSuccess])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!organization.mergeBuildingAssetLoading && organization.mergeBuildingAssetSuccess) {
            setAlertSuccessMessage(t('MERGE_ASSETS.SUCCESS'));
            setAlertSuccessState(true);
        }
        dispatch(OrganizationActions.mergeBuildingAssetUnmounted());
    }, [organization.mergeBuildingAssetLoading, organization.mergeBuildingAssetSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!organization.upsertBuildingDetailLoading && organization.upsertBuildingDetailSuccess) {
            setAlertSuccessMessage(t('TOAST_MSG.TOAST_SUCCESS_BUILDING_CREATE'));
            setAlertSuccessState(true);
        }
        dispatch(OrganizationActions.upsertBuildingUnmounted());
    }, [organization.upsertBuildingDetailLoading, organization.upsertBuildingDetailSuccess])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            !organization.orgListLoading &&
            organization.orgListSuccess &&
            !dashboard.buildingListLoading &&
            dashboard.buildingListSuccess
        ) {
            setLastRetrievedTime(new Date());
        }
    }, [
        organization.orgListLoading,
        organization.orgListSuccess,
        dashboard.buildingListLoading,
        dashboard.buildingListSuccess,
    ]);

    return (
        <div data-testid="organizations-details-container">
            <GenericToolbar
                title={orgName}
                subtitle={t('ORGANIZATION_DETAILS.ORGANIZATION_DETAILS')}
                showBackButton={!organization.orgListLoading && organization.orgListSuccess && !dashboard.buildingListLoading && dashboard.buildingListSuccess}
                backButtonOnClick={(): void => {
                    pushAppRoute(history, {
                        type: backRoute,
                    });
                }}
                color={ToolbarColor.Default}
            />

            <div data-testid="container-org-details" style={{ display: 'flex', flexWrap: 'wrap', marginTop: 16 }}>

                <div style={{ minHeight: 700, width: md ? '40%' : '80%', marginLeft: 30 }}>
                    <OrgInfo
                        orgId={orgId}
                        orgNameUpdate={(orgName: string): void => setOrgName(orgName)}
                        userOnClick={goToManageUsers}
                    />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', minHeight: 700, width: md ? '45%' : '80%', marginLeft: 30 }}>
                    <Box
                        display="flex"
                        style={{
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            height: 28,
                            width: '100%',
                            paddingRight: 8,
                            gap: 8,
                            marginBottom: 8,
                        }}
                    >
                        <LastRetrievedTimestamp lastRefresh={lastRetrievedTime} />

                        <Button
                            style={{
                                minWidth: 'auto',
                                height: 20,
                                paddingLeft: 0,
                                paddingRight: 0,
                                textTransform: 'none',
                                fontWeight: 400,
                                fontSize: 12,
                                color: PXBColors.blue[500],
                            }}
                            color="primary"
                            data-testid="org-refresh-button"
                            onClick={refreshOrgDetails}
                        >
                            <Typography
                                variant="body2"
                                style={{
                                    color: PXBColors.blue[500],
                                    fontSize: 12,
                                }}
                            >
                                {t('COMMON.SYNC')}
                            </Typography>
                        </Button>
                    </Box>
                    <Card style={{ marginBottom: 20, overflow: 'hidden' }}>
                        <OverviewMap
                            buildingId={''}
                            showAllBuildings={true}
                            showCustomControls={true}
                            mapContainerStyle={{ height: '300px', width: '100%' }}
                            onBuildingNameClicked={(selectedBuildingId: string): void => {
                                goToOrgBuildingDetails({ buildingId: selectedBuildingId, buildingName: '' });
                            }}
                        />
                    </Card>
                    <OrgBuildings
                        orgId={orgId}
                        onClick={goToOrgBuildingDetails}
                    />
                </div>
                {
                    alertSuccessState &&
                    <Snackbar
                        open={true}
                        onClose={() => setAlertSuccessState(false)}
                        autoHideDuration={5000}
                        title={''}
                        message={alertSuccessMessage}
                        action={
                            <Button color="inherit" size="small" onClick={() => setAlertSuccessState(false)}>
                                {t('ACTIONS.OKAY')}
                            </Button>
                        }
                        ContentProps={{ sx: { background: PXBColors.black[900] } }}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        sx={{ bottom: { xs: 90, sm: 0 } }}
                        style={{ height: 200, width: 200, flexGrow: 0 }}
                    />
                }
            </div>

        </div >
    );
};
